import { createFileRoute } from "@tanstack/react-router";
import { t } from "i18next";
import { Fragment, useState } from "react";
import { toast } from "sonner";

import { LayoutSimple } from "~/components/layouts/simple";
import { Button, LinkButton } from "~/components/primitives/button";
import { ElevatedBox } from "~/components/primitives/elevated-box";
import { Spinner } from "~/components/primitives/spinner";
import { useFeatureFlag } from "~/lib/feature-flags/hooks";
import { useAuthStore } from "~/modules/auth/auth-store";
import { getBillingPortalUrlMutation } from "~/modules/auth/network";
import { canOrgManagePlan, canUserManageOrgSubscription } from "~/modules/auth/permissions";
import { BuyCreditsForm } from "~/modules/checkout/components/buy-credits-form";
import { getCheckoutInfoQuery } from "~/modules/checkout/network/queries";
import { getStripeKeyMode } from "~/modules/checkout/utils";
import { CreditsBalance } from "~/modules/credits/components/credits-balance";
import { creditActionsQuery, creditsBalanceQuery } from "~/modules/credits/network/queries";
import { css, cva } from "~/styled-system/css";

export const Route = createFileRoute("/_app/settings/billing")({
  component: RouteComponent,
  loader({ context: { queryClient } }) {
    queryClient.prefetchQuery(
      getCheckoutInfoQuery.queryOptions({ params: { stripeKeyMode: getStripeKeyMode() } })
    );

    queryClient.prefetchQuery(creditsBalanceQuery.queryOptions());
  },
  pendingComponent: PendingComponent,
});

function PendingComponent() {
  return null;
}

function RouteComponent() {
  const { org } = Route.useRouteContext();
  const plan = org.subscription?.plan ?? "free";

  const { data } = getCheckoutInfoQuery.useSuspenseQuery({
    params: { stripeKeyMode: getStripeKeyMode() },
  });

  const canManageSubscriptionInStripe = useAuthStore(
    (s) => canUserManageOrgSubscription(s.user) && canOrgManagePlan(s.currentOrg)
  );

  const { mutate: openBillingPortal, isPending } = getBillingPortalUrlMutation.useMutation({
    onSuccess({ url }) {
      if (url) {
        window.open(url, "_blank");
      }
    },
    onError(error) {
      console.error(error);
      toast.error(t("mirage-studio:billing.manage-plan-error"));
    },
  });

  const onManagePlan = () => {
    openBillingPortal({ params: { returnUrl: window.location.href } });
  };

  const { enabled: isCreditsUsageHistoryEnabled } = useFeatureFlag(
    "studio_enable_credits_usage_history"
  );

  return (
    <LayoutSimple title={t("mirage-studio:sidebar.settings.billing")}>
      <ElevatedBox.Root withDividers>
        <ElevatedBox.Item
          className={css({ display: "flex", justifyContent: "space-between", gap: 8 })}
        >
          <div className={css({ display: "flex", flexDirection: "column" })}>
            <span className={elevatedBoxItemHeading}>{t("mirage-studio:billing.plan")}</span>
            <span className={css({ textTransform: "capitalize" })}>{plan}</span>
            {org.subscription?.cancelAtPeriodEnd && org.subscription.currentPeriodEnd && (
              <p
                className={css({
                  textStyle: "body-3-intense",
                  color: "$error",
                  marginTop: "4px",
                })}
              >
                {t("mirage-studio:billing.subscription-will-cancel-on", {
                  date: formatDate(org.subscription.currentPeriodEnd),
                })}
              </p>
            )}
          </div>
          {canManageSubscriptionInStripe && (
            <LinkButton variant="tertiary" size="md" onClick={onManagePlan} isPending={isPending}>
              {t("mirage-studio:billing.manage-plan")}
            </LinkButton>
          )}
        </ElevatedBox.Item>
        <ElevatedBox.Item
          className={css({
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 24,
          })}
        >
          <span className={elevatedBoxItemHeading}>{t("mirage-studio:billing.credits")}</span>
          <CreditsBalance />
        </ElevatedBox.Item>
        <ElevatedBox.Item
          className={css({
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          })}
        >
          <BuyCreditsForm
            paymentMethodId={data.paymentMethodDetails?.id}
            priceCents={data.credits.priceCents}
            location="billing"
          />
        </ElevatedBox.Item>
      </ElevatedBox.Root>
      {isCreditsUsageHistoryEnabled && <CreditsUsageHistory />}
    </LayoutSimple>
  );
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const elevatedBoxItemHeading = css({
  textStyle: "body-2-intense",
  color: "$gray.500",
});

const FILTER_OPTIONS = {
  "1d": {
    label: () => "1d",
    lookbackDays: 1,
  },
  "7d": {
    label: () => "7d",
    lookbackDays: 7,
  },
  "30d": {
    label: () => "30d",
    lookbackDays: 30,
  },
  "all-time": {
    label: () => t("mirage-studio:billing.credit-history.all-time"),
    lookbackDays: undefined,
  },
} as const;

type FilterOption = keyof typeof FILTER_OPTIONS;

function CreditsUsageHistory() {
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>("all-time");

  const lookbackDays = FILTER_OPTIONS[selectedFilter].lookbackDays;
  const { data, isPending, isError } = creditActionsQuery.useQuery({
    params: lookbackDays ? { lookbackDays } : {}, // omit the parameter entirely rather than passing ?param=undefined
  });

  return (
    <Fragment>
      <header className={headerStyles}>
        <span>{t("mirage-studio:billing.credit-history.title")}</span>
        <div className={css({ display: "flex", gap: 8 })}>
          {Object.entries(FILTER_OPTIONS).map(([value, option]) => (
            <Button
              key={value}
              variant={selectedFilter === value ? "secondary" : "tertiary"}
              size="md"
              onClick={() => setSelectedFilter(value as FilterOption)}
            >
              {option.label()}
            </Button>
          ))}
        </div>
      </header>
      <ElevatedBox.Root>
        <ElevatedBox.Item className={css({ padding: 0 })}>
          <table className={tableStyles}>
            <thead>
              <tr>
                <th>{t("common:date")}</th>
                <th>{t("common:name")}</th>
                <th>{t("common:type")}</th>
                <th>{t("mirage-studio:billing.credits")}</th>
              </tr>
            </thead>
            <tbody>
              {isPending && (
                <tr>
                  <td colSpan={4}>
                    <Spinner className={css({ marginX: "auto" })} />
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={4} className={css({ color: "$error", textAlign: "center" })}>
                    {t("mirage-studio:billing.credit-history.failed-to-load")}
                  </td>
                </tr>
              )}
              {data?.actions.length === 0 && (
                <tr>
                  <td colSpan={4} className={css({ color: "$gray.500", textAlign: "center" })}>
                    {t("mirage-studio:billing.credit-history.empty")}
                  </td>
                </tr>
              )}
              {data?.actions.map((creditAction) => {
                return (
                  <tr key={creditAction.actionTimestamp}>
                    <td>{formatDate(creditAction.actionTimestamp)}</td>
                    <td>{creditAction.userName}</td>
                    <td>{creditAction.actionDescription}</td>
                    <td className={creditsStyle({ positive: creditAction.credits > 0 })}>
                      {formatCredits(creditAction.credits)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ElevatedBox.Item>
      </ElevatedBox.Root>
    </Fragment>
  );
}

const headerStyles = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
});

const tableStyles = css({
  width: "100%",
  "& th": {
    padding: "12px 16px",
    color: "$gray.500",
    textStyle: "body-3-caps",
    textAlign: "left",
  },
  "& td": {
    padding: "22px 16px",
  },
  "& tr": {
    borderBottom: "1px solid token(colors.$surface.highlight)",
  },
  "& tbody > tr:last-of-type": {
    borderBottom: "none",
  },
});

const creditsStyle = cva({
  variants: {
    positive: {
      true: {
        color: "$eucalyptus",
      },
    },
  },
});

function formatCredits(credits: number) {
  if (credits <= 0) {
    return `${credits} ${t("mirage-studio:credits-balance.credits")}`;
  } else {
    return `+${credits} ${t("mirage-studio:credits-balance.credits")}`;
  }
}
